import React from "react";
import { X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ModalWrapper from "../Modal";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { deleteProduct } from "../../services/productService";

const DeleteModal = ({
  isOpen,
  onClose,
  onDelete,
  product,
  onDeleteSuccess,
}) => {
  const { mutate: handleDelete, isPending } = useMutation({
    mutationFn: () => deleteProduct(product.id),
    onSuccess: () => {
      toast.success("Product deleted successfully!");
      onClose();
      if (onDeleteSuccess) onDeleteSuccess();
      if (onDelete) onDelete();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete product."));
    },
  });
  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="w-full max-w-[480px] relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-2 right-4 text-gray-[#4F535D] hover:text-gray-600 transition-colors"
        >
          <X size={24} />
        </button>

        {/* Header Icon */}
        <div className="mt-[22px] mb-6 flex justify-center">
          <img
            src="/images/icons/delete-product.svg"
            alt="delete"
            className="h-16 w-16"
          />
        </div>

        {/* Text Content */}
        <div className="mb-6 text-center">
          <h2 className="mb-3 text-2xl font-semibold text-[#0C0C0E]">
            Confirm Delete
          </h2>
          <p className="text-base text-[#7D828D] font-medium">
            Are you sure you want to delete the product <br />
            <span className="font-medium text-[#0C0C0E]">{product.title}</span>?
          </p>
        </div>

        {/* Product Preview Card */}
        <div className="mb-6 flex items-center gap-4 rounded-[14px] border border-[#ECEDEF] px-3 py-2">
          <div className="flex flex-1 flex-col">
            <span className="text-base font-medium text-[#0C0C0E]">
              {product?.title || "Unknown Product"}
            </span>
            <span className="text-[#7D828D] text-sm font-medium">
              {product?.sku || "N/A"}
            </span>
          </div>
          <div className="flex flex-col text-right">
            <span className="text-base font-medium text-[#0C0C0E]">
              ₦{product?.price || 0}
            </span>
            <span className="text-[#7D828D] text-sm font-medium">
              Stock: {product?.quantity || product?.stock || 0}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={() => handleDelete()}
            disabled={isPending}
            className="flex-1 rounded-lg h-10 bg-[#DB0003] py-2 text-base font-medium text-white transition-all hover:bg-red-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Deleting..." : "Delete"}
          </button>
          <button
            onClick={onClose}
            className="flex-1 rounded-lg h-10 border border-gray-200 bg-white py-2 text-base font-medium text-gray-700 transition-all hover:bg-gray-50 active:scale-95"
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default DeleteModal;
